import { IMAGE_FILE_TYPES, PRODUCTS_BINDING_INPUT } from "@/libs/builder/consts";

export const navLink = [
  {
    name: "title",
    type: "string",
    required: true,
  },
  {
    name: "url",
    type: "url",
    required: true,
  },
  {
    name: "secondaryNavigation",
    type: "reference",
    model: "secondary-navigation",
  },
  {
    name: "isLoyalty",
    type: "boolean",
  },
  {
    name: "loyaltyPointsCurrency",
    helperText: "Label for loyalty points currency",
    showIf: 'options.get("isLoyalty") === true',
    type: "string",
  },
  {
    name: "chevronAriaLabel",
    helperText: "Accessible label for the submenu toggle button",
    type: "string",
  },
] as const satisfies Inputs;

export const headerInputs = [
  {
    name: "logo",
    type: "object",
    defaultValue: {
      link: "/",
      srcDark: "",
      srcLight: "",
      title: "",
      alt: "Logo",
    },
    required: true,
    subFields: [
      {
        name: "link",
        type: "url",
        required: true,
      },
      {
        name: "srcDark",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "srcLight",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "alt",
        type: "string",
        required: true,
      },
      {
        name: "width",
        type: "number",
      },
    ],
  },
  {
    name: "navLinks",
    type: "list",
    defaultValue: [
      {
        title: "Home",
        url: "/",
      },
      {
        title: "About",
        url: "/about",
      },
      {
        title: "Shop",
        url: "/",
      },
      {
        title: "Ploom Club",
        url: "/loyalty",
        isLoyalty: true,
      },
    ],
    subFields: navLink,
  },
  {
    name: "enableUserIndication",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "enableUserFullNameForIndication",
    type: "boolean",
    defaultValue: false,
    showIf: 'options.get("enableUserIndication") === true',
  },
  {
    name: "accountDropdownSettings",
    type: "object",
    defaultValue: {
      logoutRedirectPage: "/",
      logoutLabel: "Sign out",
      greetingMessage: "Hi,",
      hideGreetingMessage: false,
      profileLink: "/profile",
      accountDashboardLabel: "Account Dashboard",
      ordersLabel: "My orders",
      paymentsLabel: "My payment methods",
      accountInformationLabel: "Account information",
      packCodesLink: "/pack-codes",
      packCodesLabel: "Pack codes",
    },
    subFields: [
      {
        name: "logoutRedirectPage",
        type: "url",
        required: true,
      },
      {
        name: "logoutLabel",
        type: "string",
        required: true,
      },
      {
        name: "greetingMessage",
        type: "string",
      },
      {
        name: "hideGreetingMessage",
        type: "boolean",
      },
      {
        name: "profileLink",
        type: "url",
      },
      {
        name: "accountDashboardLabel",
        type: "string",
      },
      {
        name: "accountDashboardLink",
        helperText: "Overrides the default `profileLink + '#/'` destination",
        type: "url",
      },
      {
        name: "ordersLabel",
        type: "string",
      },
      {
        name: "ordersLink",
        helperText: "Overrides the default `profileLink + '#/orders'` destination",
        type: "url",
      },
      {
        name: "paymentsLabel",
        type: "string",
      },
      {
        name: "paymentsLink",
        helperText: "Overrides the default `profileLink + '#/payments'` destination",
        type: "url",
      },
      {
        name: "shopButtonLabel",
        type: "string",
        helperText: "Label for empty cart button in Mini Cart",
      },
      {
        name: "accountInformationLabel",
        type: "string",
      },
      {
        name: "accountInformationLink",
        helperText: "Overrides the default `profileLink + '#/account'` destination",
        type: "url",
      },
      {
        name: "packCodesLink",
        type: "url",
      },
      {
        name: "packCodesLabel",
        type: "string",
      },
      {
        name: "tierTranslations",
        type: "list",
        helperText: "Map of tier names to their translated display labels (e.g. silver → Silver level)",
        subFields: [
          {
            name: "tier",
            type: "string",
            required: true,
            helperText: "Tier key (e.g. silver, gold, black)",
          },
          {
            name: "label",
            type: "string",
            required: true,
            helperText: "Translated display label",
          },
        ],
      },
      {
        name: "hideLoyaltyTiers",
        type: "boolean",
        defaultValue: false,
        helperText: "Hide the loyalty tier label in the account banner, showing only points",
      },
    ],
  },
  {
    name: "tools",
    type: "object",
    defaultValue: {
      accountLink: "/",
      accountLabel: "Account",
      accountLoadingLabel: "Loading account",
      cartLink: "/",
      storeLocatorLink: "/",
      storeLocatorLabel: "Store locator",
      mobileAppLink: "/",
      resultsLink: "/",
      cartLabel: "Cart",
      openMenuLabel: "Open menu",
      closeMenuLabel: "Close menu",
      navAriaLabel: "Navigation",
      languageLabel: "Language",
      searchLabel: "Search",
      expandBreadcrumbsLabel: "Show all breadcrumbs",
      loyaltyPointsCurrency: "pts",
    },
    required: true,
    subFields: [
      {
        name: "accountLink",
        type: "url",
      },
      {
        name: "accountLabel",
        type: "string",
      },
      {
        name: "accountLoadingLabel",
        helperText: "Accessible label for the loading indicator shown while the account status is pending",
        type: "string",
      },
      {
        name: "storeLocatorLink",
        type: "url",
      },
      {
        name: "storeLocatorLabel",
        type: "string",
      },
      {
        name: "mobileAppLink",
        type: "url",
      },
      {
        name: "resultsLink",
        type: "url",
      },
      {
        name: "cartLabel",
        type: "string",
      },
      {
        name: "openMenuLabel",
        helperText: "Accessible label for the menu button when the navigation is closed",
        type: "string",
      },
      {
        name: "closeMenuLabel",
        helperText: "Accessible label for the menu button when the navigation is open",
        type: "string",
      },
      {
        name: "navAriaLabel",
        helperText: "Accessible label for the mobile navigation region",
        type: "string",
      },
      {
        name: "languageLabel",
        helperText: "Label for the language selector in the mobile navigation",
        type: "string",
      },
      {
        name: "searchLabel",
        helperText: "Label for the search action in the mobile navigation",
        type: "string",
      },
      {
        name: "expandBreadcrumbsLabel",
        helperText: "Accessible label for the button that expands the collapsed breadcrumbs",
        type: "string",
      },
      {
        name: "loyaltyPointsCurrency",
        helperText: "Label for loyalty points currency in the mobile navigation",
        type: "string",
      },
    ],
  },
  PRODUCTS_BINDING_INPUT,
] as const satisfies Inputs;

export type HeaderProps = BuilderComponentProps<typeof headerInputs>;
